/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const HemisphereLightCtor = (typeof globalThis !== "undefined" && globalThis.HemisphereLightNode) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const HEMISPHERE_LIGHT_PROPS = ["skyColor", "groundColor", "intensity"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class HemisphereLightEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    HEMISPHERE_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          
          // Special handling for color properties
          if ((prop === "skyColor" || prop === "groundColor") && this.sourceObject?.[prop]?.set) {
            this.sourceObject[prop].set(v);
          } else if ((prop === "skyColor" || prop === "groundColor") && this.sourceObject?.[prop]?.isColor) {
            // Convert hex colors to THREE.Color for editor context
            if (typeof v === "string" || typeof v === "number") {
              this.sourceObject[prop].set(v);
            } else if (v?.isColor) {
              this.sourceObject[prop].copy(v);
            }
          } else if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
          
          // Call onChange if available
          if (typeof this.sourceObject.onChange === "function") {
            this.sourceObject.onChange();
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
        
        // For color properties, ensure we store the actual THREE.Color object
        if ((prop === "skyColor" || prop === "groundColor") && sourceObject?.[prop]?.isColor) {
          initial = sourceObject[prop];
        }
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class HemisphereLightAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const HEMISPHERE_LIGHT_COMPONENT = "hemisphere-light";

    const setComponentProp = (component, key, val) => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(component) || {})
      };
      current[key] = val;
      this.sourceObject.el.setAttribute(component, current);
    };

    const getComponentProp = (component, key) =>
      this.sourceObject?.el?.getAttribute(component)?.[key];

    HEMISPHERE_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(HEMISPHERE_LIGHT_COMPONENT, prop),
        set: v => setComponentProp(HEMISPHERE_LIGHT_COMPONENT, prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of HEMISPHERE_LIGHT_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new HemisphereLightAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new HemisphereLightEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class HemisphereLightElement extends ScriptableObject {
  static scriptableType = "hemisphere-light";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      
      el.setAttribute("hemisphere-light", {
        skyColor: initial.skyColor || "#87ceeb",
        groundColor: initial.groundColor || "#90ee90",
        intensity: initial.intensity !== undefined ? initial.intensity : 1
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real HemisphereLightNode and upgrade to scriptable
    const node = createHemisphereLightElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public hemisphere light props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if ((prop === "skyColor" || prop === "groundColor") && val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of HEMISPHERE_LIGHT_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of HEMISPHERE_LIGHT_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if ((p === "skyColor" || p === "groundColor") && v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new HemisphereLightElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor HemisphereLightNode instance to Scriptable, attach adapter and proxies
function makeEditorHemisphereLightNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || HemisphereLightCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: HemisphereLightElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = HemisphereLightElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new HemisphereLightEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new HemisphereLightEditorAdapter(node, descriptors);
  }

  for (const prop of HEMISPHERE_LIGHT_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  // Ensure HemisphereLightElement's enhanced `clone` logic is used for
  // editor-created hemisphere light nodes. Without this, the instance would fall back to
  // HemisphereLightNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== HemisphereLightElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: HemisphereLightElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = HemisphereLightElement.prototype.clone;
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
HEMISPHERE_LIGHT_PROPS.forEach(prop => {
  Object.defineProperty(HemisphereLightElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createHemisphereLightElementEditor(initial = {}) {
  const HemisphereLightCtor = (typeof globalThis !== "undefined" && globalThis.HemisphereLightNode) || undefined;
  
  if (typeof HemisphereLightCtor !== "function") {
    throw new Error("HemisphereLightElementEditor: HemisphereLightNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new HemisphereLightCtor(editor);
  } catch (e) {
    throw new Error(`HemisphereLightElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorHemisphereLightNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      // Handle special cases for initial color values
      if ((k === "skyColor" || k === "groundColor") && node[k]?.isColor) {
        if (typeof initial[k] === "string" || typeof initial[k] === "number") {
          node[k].set(initial[k]);
        } else if (initial[k]?.isColor) {
          node[k].copy(initial[k]);
        }
      } else {
        node[k] = initial[k];
      }
    }
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("hemisphere-light", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== HemisphereLightElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return HemisphereLightElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorHemisphereLightNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { HemisphereLightAFrameAdapter, HemisphereLightEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(HemisphereLightElement.prototype, Symbol.toStringTag, { value: "HemisphereLightElement" });